import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCheckCircle,
  FaArrowRight,
  FaUpload,
  FaTimes,
  FaFileAlt,
  FaUserCheck,
  FaPalette,
  FaTrophy,
  FaShieldAlt,
  FaPlus,
  FaMapPin, // Add this
  FaLocationArrow, // Add this
  FaInfoCircle, // Add this
} from 'react-icons/fa';
import '../styles/artisanjoin.scss';
import { toast } from 'react-toastify';
import FurnitureNavbar from '../components/layout/Nav';
import { serverUrlAuth } from '../serverUrl';

const ArtisanJoinPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    yearsOfExperience: '',
    relevantDocuments: [], // Changed to array
    portfolio: '',
    bio: '',
    location: '',

    latitude: '',
    longitude: '',
  });

  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const benefits = [
    {
      icon: <FaUserCheck />,
      title: 'Global Exposure',
      description: 'Showcase your work to customers worldwide',
    },
    {
      icon: <FaPalette />,
      title: 'Creative Freedom',
      description: 'Set your own prices and maintain artistic control',
    },
    {
      icon: <FaTrophy />,
      title: 'Fair Commission',
      description: 'Competitive commission rates and timely payments',
    },
    {
      icon: <FaShieldAlt />,
      title: 'Secure Platform',
      description: 'Protected payments and intellectual property rights',
    },
  ];

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        setFormData((prev) => ({
          ...prev,
          latitude,
          longitude,
        }));

        toast.success('Your location has been captured successfully.');
      },
      (error) => {
        console.error('Location error:', error);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error(
              'Location permission was denied. Please allow location access to continue.',
            );
            break;

          case error.POSITION_UNAVAILABLE:
            toast.error('Your location could not be determined.');
            break;

          case error.TIMEOUT:
            toast.error('Location request timed out. Please try again.');
            break;

          default:
            toast.error('Unable to get your location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate each file
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/jpg',
      ];

      if (!allowedTypes.includes(file.type)) {
        invalidFiles.push(`${file.name} (Invalid type)`);
        return;
      }

      // Validate file size (max 10MB each)
      if (file.size > 10 * 1024 * 1024) {
        invalidFiles.push(`${file.name} (Exceeds 10MB limit)`);
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Cannot upload: ${invalidFiles.join(', ')}`);
    }

    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        relevantDocuments: [...prev.relevantDocuments, ...validFiles],
      }));
      setErrors((prev) => ({ ...prev, relevantDocuments: '' }));
      toast.success(`${validFiles.length} file(s) added successfully`);
    }
  };

  const removeFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      relevantDocuments: prev.relevantDocuments.filter((_, i) => i !== index),
    }));
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word')) return '📝';
    if (fileType.includes('image')) return '🖼️';
    return '📎';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (
      !/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(
        formData.phoneNumber,
      )
    ) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
         // Add location validation
  if (!formData.location.trim()) {
    newErrors.location = 'Location is required. Please enter your city or use your current location.';
  }

    if (!formData.yearsOfExperience) {
      newErrors.yearsOfExperience = 'Years of experience is required';
    } else if (
      formData.yearsOfExperience < 0 ||
      formData.yearsOfExperience > 100
    ) {
      newErrors.yearsOfExperience =
        'Please enter a valid number between 0 and 100';
    }

    if (formData.relevantDocuments.length === 0) {
      newErrors.relevantDocuments =
        'Please upload at least one document (portfolio, certifications, etc.)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone_number', formData.phoneNumber);
      formDataToSend.append('years_of_experience', formData.yearsOfExperience);
      formDataToSend.append('portfolio_url', formData.portfolio);
      formDataToSend.append('bio', formData.bio);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('latitude', formData.latitude);
      formDataToSend.append('longitude', formData.longitude);

      // Append multiple documents with the same field name
      formData.relevantDocuments.forEach((doc, index) => {
        formDataToSend.append(`documents`, doc);
      });

      const response = await serverUrlAuth.post(
        'artisan-applications/apply-to-be-an-artisan/',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setUploadProgress(percentCompleted);
            }
          },
        },
      );

      setUploadProgress(100);

      if (response.status === 200 || response.status === 201) {
        setSuccess(true);
        toast.success(
          'Application submitted successfully! We will review your application and get back to you within 48 hours.',
        );

        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phoneNumber: '',
            yearsOfExperience: '',
            relevantDocuments: [],
            portfolio: '',
            bio: '',
          });
          setUploadProgress(0);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setUploadProgress(0);

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          const errorMessages = [];
          Object.keys(error.response.data).forEach((key) => {
            if (Array.isArray(error.response.data[key])) {
              errorMessages.push(...error.response.data[key]);
            } else if (typeof error.response.data[key] === 'string') {
              errorMessages.push(error.response.data[key]);
            }
          });
          if (errorMessages.length > 0) {
            toast.error(errorMessages[0]);
          } else {
            toast.error('Failed to submit application. Please try again.');
          }
        } else {
          toast.error('Failed to submit application. Please try again.');
        }
      } else {
        toast.error(
          'Failed to submit application. Please check your connection and try again.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <FurnitureNavbar />
        <div className="artisan-join-page success-page">
          <div className="success-container">
            <div className="success-icon">
              <FaCheckCircle />
            </div>
            <h1>Application Submitted Successfully!</h1>
            <p>
              Thank you for your interest in joining the Artiza community as an
              artisan. Our team will review your application and get back to you
              within 48 hours .
            </p>
            <div className="success-actions">
              <Link to="/" className="home-btn">
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <FurnitureNavbar />
      <div className="artisan-join-page">
        {/* Hero Section */}
        <div className="join-hero">
          <div className="hero-content">
            <h1>Join Artiza as an Artisan</h1>
            <p>
              Showcase your unique creations to the world and connect with art
              lovers globally
            </p>
          </div>
        </div>

        <div className="join-container">
          {/* Benefits Section */}
          <div className="benefits-section">
            <h2>Why Join Artiza?</h2>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-card">
                  <div className="benefit-icon">{benefit.icon}</div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="join-content">
            {/* Guidelines Section */}
            <div className="guidelines-section">
              <h2>Artisan Guidelines</h2>
              <div className="guidelines-list">
                <div className="guideline-item">
                  <div className="guideline-number">01</div>
                  <div className="guideline-text">
                    <h3>Authentic Handcrafted Work</h3>
                    <p>
                      All products must be handmade or hand-finished by you.
                      Mass-produced items are not permitted.
                    </p>
                  </div>
                </div>
                <div className="guideline-item">
                  <div className="guideline-number">02</div>
                  <div className="guideline-text">
                    <h3>Quality Standards</h3>
                    <p>
                      Maintain high-quality craftsmanship and use durable, safe
                      materials in your creations.
                    </p>
                  </div>
                </div>
                <div className="guideline-item">
                  <div className="guideline-number">03</div>
                  <div className="guideline-text">
                    <h3>Original Designs</h3>
                    <p>
                      Submit only original artwork. Copyright infringement or
                      plagiarism is strictly prohibited.
                    </p>
                  </div>
                </div>
                <div className="guideline-item">
                  <div className="guideline-number">04</div>
                  <div className="guideline-text">
                    <h3>Timely Delivery</h3>
                    <p>
                      Commit to fulfilling orders within the promised timeframe.
                      Communication is key.
                    </p>
                  </div>
                </div>
                <div className="guideline-item">
                  <div className="guideline-number">05</div>
                  <div className="guideline-text">
                    <h3>Professional Conduct</h3>
                    <p>
                      Maintain professional communication with customers and
                      adhere to platform policies.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Application Form */}
            <div className="form-section">
              <h2>Apply to Become an Artisan</h2>
              <p className="form-subtitle">
                Fill out the form below to start your journey with Artiza
              </p>

              <form onSubmit={handleSubmit} className="artisan-form">
                {/* Name Field */}
                <div className="form-group">
                  <label htmlFor="name">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && (
                    <span className="error-message">{errors.name}</span>
                  )}
                </div>

                {/* Email Field */}
                <div className="form-group">
                  <label htmlFor="email">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>

                {/* Phone Number Field */}
                <div className="form-group">
                  <label htmlFor="phoneNumber">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+1234567890"
                    className={errors.phoneNumber ? 'error' : ''}
                  />
                  {errors.phoneNumber && (
                    <span className="error-message">{errors.phoneNumber}</span>
                  )}
                </div>
                {/* Location Field with Improved Design */}
                <div className="form-group location-group">
                  <label htmlFor="location">
                    Location <span className="required">*</span>
                  </label>

                  <div className="location-input-wrapper">
                    <div className="location-input-container">
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Enter your city, state, or country"
                        className={errors.location ? 'error' : ''}
                      />
                      {formData.location && (
                        <button
                          type="button"
                          className="clear-location-btn"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              location: '',
                              latitude: '',
                              longitude: '',
                            }));
                          }}
                          aria-label="Clear location"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={getUserLocation}
                      className={`location-btn ${formData.latitude && formData.longitude ? 'active' : ''}`}
                      disabled={loading}
                    >
                      {formData.latitude && formData.longitude ? (
                        <>
                          <FaCheckCircle className="btn-icon" />
                          <span>Location Detected</span>
                        </>
                      ) : (
                        <>
                          <FaLocationArrow className="btn-icon" />
                          <span>Use My Current Location</span>
                        </>
                      )}
                    </button>
                  </div>

                  {formData.latitude && formData.longitude && (
                    <div className="location-success">
                      <FaCheckCircle className="success-icon" />
                      <div className="success-content">
                        <span className="success-title">
                          Location captured successfully
                        </span>
                        <span className="success-coords">
                          {formData.latitude.toFixed(6)},{' '}
                          {formData.longitude.toFixed(6)}
                        </span>
                      </div>
                    </div>
                  )}

                  {errors.location && (
                    <span className="error-message">{errors.location}</span>
                  )}

                  <small className="helper-text">
                    <FaInfoCircle className="helper-icon" />
                    Enter your location manually or click the button to use your
                    current location
                  </small>
                </div>

                {/* Years of Experience */}
                <div className="form-group">
                  <label htmlFor="yearsOfExperience">
                    Years of Experience <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="yearsOfExperience"
                    name="yearsOfExperience"
                    value={formData.yearsOfExperience}
                    onChange={handleChange}
                    placeholder="e.g., 5"
                    min="0"
                    max="100"
                    className={errors.yearsOfExperience ? 'error' : ''}
                  />
                  {errors.yearsOfExperience && (
                    <span className="error-message">
                      {errors.yearsOfExperience}
                    </span>
                  )}
                </div>

                {/* Portfolio URL (Optional) */}
                <div className="form-group">
                  <label htmlFor="portfolio">Portfolio URL (Optional)</label>
                  <input
                    type="url"
                    id="portfolio"
                    name="portfolio"
                    value={formData.portfolio}
                    onChange={handleChange}
                    placeholder="https://yourportfolio.com"
                  />
                  <small>
                    Link to your online portfolio, Instagram, or website
                  </small>
                </div>

                {/* Bio (Optional) */}
                <div className="form-group">
                  <label htmlFor="bio">Short Bio (Optional)</label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Tell us about your artistic journey, inspiration, and craftsmanship..."
                  />
                </div>

                {/* Multiple Documents Upload */}
                <div className="form-group">
                  <label htmlFor="relevantDocuments">
                    Relevant Documents <span className="required">*</span>
                  </label>
                  <div
                    className={`file-upload-area ${errors.relevantDocuments ? 'error' : ''}`}
                  >
                    <input
                      type="file"
                      id="relevantDocuments"
                      name="relevantDocuments"
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="relevantDocuments"
                      className="file-upload-label"
                    >
                      <FaUpload />
                      <span>Click to upload files</span>
                      <small>or drag and drop</small>
                      <small className="file-limits">
                        Supported: PDF, DOC, DOCX, JPEG, PNG (Max 10MB each)
                      </small>
                    </label>

                    {/* File List Preview */}
                    {formData.relevantDocuments.length > 0 && (
                      <div className="file-list">
                        <div className="file-list-header">
                          <strong>
                            {formData.relevantDocuments.length} file(s) selected
                          </strong>
                          <button
                            type="button"
                            className="add-more-btn"
                            onClick={() =>
                              document
                                .getElementById('relevantDocuments')
                                .click()
                            }
                          >
                            <FaPlus /> Add More
                          </button>
                        </div>
                        <div className="file-items">
                          {formData.relevantDocuments.map((file, index) => (
                            <div key={index} className="file-preview-item">
                              <div className="file-info">
                                <span className="file-icon">
                                  {getFileIcon(file.type)}
                                </span>
                                <div className="file-details">
                                  <span className="file-name">{file.name}</span>
                                  <small className="file-size">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </small>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => removeFile(index)}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.relevantDocuments && (
                    <span className="error-message">
                      {errors.relevantDocuments}
                    </span>
                  )}
                  <small className="helper-text">
                    Upload portfolio samples, certificates, or previous work
                    examples. You can upload multiple files.
                  </small>
                </div>

                {/* Upload Progress */}
                {loading && uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <span>{uploadProgress}% uploaded</span>
                  </div>
                )}

                {/* Submit Button */}
                <div className="form-actions">
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-small"></span>
                        Submitting Application...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <FaArrowRight />
                      </>
                    )}
                  </button>
                </div>

                <div className="form-footer">
                  <p>
                    By submitting this application, you agree to our{' '}
                    <Link to="/terms">Terms of Service</Link> and{' '}
                    <Link to="/privacy">Privacy Policy</Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArtisanJoinPage;
